import { t } from "../i18n";
import { $, h } from "../lib/dom";
import { type Cell, key, unkey } from "../lib/util";
import { clearMovement, P } from "./player";
import { clerkSprite, drawChar, interactName } from "./render";
import { L, S } from "./state";
import { clock, idleSecNow } from "./time";
import { bg, interact, T } from "./world";
import { closeZoom } from "./zoom";

/** Levels whose help sheet was shown automatically this page load. */
const helpSeen = new Set<string>();
let helpAfter: (() => void) | null = null;

export const helpOpen = (): boolean => $("#help").classList.contains("show");

interface Group {
  id: string;
  cells: Cell[];
  x: number;
  y: number;
}

/** Interaction ids with their cells, in reading order (for numbering). */
function helpGroups(): Group[] {
  const g = new Map<string, Cell[]>();
  for (const [k, id] of interact) {
    const cells = g.get(id) ?? [];
    cells.push(unkey(k));
    g.set(id, cells);
  }
  return [...g.entries()]
    .map(([id, cells]) => ({
      id,
      cells,
      y: Math.min(...cells.map((c) => c[1])),
      x: Math.min(...cells.map((c) => c[0])),
    }))
    .sort((a, b) => a.y - b.y || a.x - b.x);
}

const SVG_NS = "http://www.w3.org/2000/svg";

function drawHelpMap(groups: Group[]): void {
  const c = $<HTMLCanvasElement>("#hmc").getContext("2d");
  if (!c) return;
  c.setTransform(3, 0, 0, 3, 0, 0);
  c.imageSmoothingEnabled = false;
  c.clearRect(0, 0, 320, 192);
  c.drawImage(bg, 0, 0);
  L.drawBelow?.(c, performance.now());
  // A still: entities at their tile positions, the player circled.
  const list = S.ents
    .filter((e) => !e.hidden)
    .map((e) => ({ k: e.sprite ?? e.k, x: e.x, y: e.y, dir: e.face ?? e.dir, fx2: e.fx2, me: false }));
  list.push({ k: clerkSprite(), x: P.x, y: P.y, dir: P.dir, fx2: undefined, me: true });
  for (const o of list.sort((a, b) => a.y - b.y)) {
    if (o.me) {
      c.strokeStyle = "#ffd447";
      c.lineWidth = 1;
      c.beginPath();
      c.ellipse(o.x * T + 8, o.y * T + 14, 9, 4, 0, 0, Math.PI * 2);
      c.stroke();
    }
    drawChar(c, o.k, o.x, o.y, o.dir, false, 0, o.fx2);
  }
  c.fillStyle = "rgba(5,7,26,.30)";
  c.fillRect(0, 0, 320, 192);

  const svg = $("#hmsvg") as unknown as SVGSVGElement;
  svg.replaceChildren();
  const el = (tag: string, attrs: Record<string, string | number>, text?: string): void => {
    const e = document.createElementNS(SVG_NS, tag);
    for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, String(v));
    if (text !== undefined) e.textContent = text;
    svg.appendChild(e);
  };
  const warn = L.helpWarn ?? [];
  groups.forEach((gr, i) => {
    const red = warn.includes(gr.id);
    const col = red ? "#ff5a6a" : "#ffd447";
    const set = new Set(gr.cells.map(([x, y]) => key(x, y)));
    // Connected cells get a traced silhouette; scattered ones a dashed bounding box.
    const first = gr.cells[0];
    if (!first) return;
    const seen = new Set([key(...first)]);
    const q: Cell[] = [first];
    while (q.length) {
      const [x, y] = q.shift() as Cell;
      for (const [dx, dy] of [
        [1, 0],
        [-1, 0],
        [0, 1],
        [0, -1],
      ] as const) {
        const k = key(x + dx, y + dy);
        if (set.has(k) && !seen.has(k)) {
          seen.add(k);
          q.push([x + dx, y + dy]);
        }
      }
    }
    const xs = gr.cells.map((c) => c[0]);
    const ys = gr.cells.map((c) => c[1]);
    const bx0 = Math.min(...xs) * T;
    const by0 = Math.min(...ys) * T;
    const bx1 = (Math.max(...xs) + 1) * T;
    const by1 = (Math.max(...ys) + 1) * T;
    if (seen.size === set.size) {
      let fill = "";
      let edge = "";
      for (const [x, y] of gr.cells) {
        const X = x * T;
        const Y = y * T;
        fill += `M${X} ${Y}h${T}v${T}h${-T}z`;
        if (!set.has(key(x, y - 1))) edge += `M${X} ${Y}h${T}`;
        if (!set.has(key(x, y + 1))) edge += `M${X} ${Y + T}h${T}`;
        if (!set.has(key(x - 1, y))) edge += `M${X} ${Y}v${T}`;
        if (!set.has(key(x + 1, y))) edge += `M${X + T} ${Y}v${T}`;
      }
      el("path", { d: fill, fill: col, "fill-opacity": "0.12", stroke: "none" });
      el("path", {
        d: edge,
        fill: "none",
        stroke: col,
        "stroke-width": "2",
        "vector-effect": "non-scaling-stroke",
        "stroke-linecap": "square",
      });
    } else {
      el("rect", {
        x: bx0,
        y: by0,
        width: bx1 - bx0,
        height: by1 - by0,
        fill: col,
        "fill-opacity": "0.12",
        stroke: col,
        "stroke-width": "2",
        "vector-effect": "non-scaling-stroke",
        "stroke-dasharray": "4 3",
      });
    }
    const n = String(i + 1);
    const w = n.length > 1 ? 13 : 9;
    const tyTop = Math.min(...gr.cells.filter((c) => c[0] === gr.x).map((c) => c[1])) * T;
    const bx = Math.max(0, gr.x * T - 2);
    const by = Math.max(0, tyTop - 7);
    el("rect", {
      x: bx,
      y: by,
      width: w,
      height: 10,
      rx: 1.5,
      fill: col,
      stroke: "#1b1426",
      "stroke-width": "1",
      "vector-effect": "non-scaling-stroke",
    });
    el(
      "text",
      { x: bx + w / 2, y: by + 8.2, "text-anchor": "middle", "font-size": "8.5", fill: red ? "#ffffff" : "#1b1426" },
      n,
    );
  });
  const px = P.x * T + 8;
  const py = P.y * T + 21;
  el("rect", {
    x: px - 15,
    y: py - 7.5,
    width: 30,
    height: 10,
    rx: 1.5,
    fill: "#1b1426",
    stroke: "#ffd447",
    "stroke-width": "1",
    "vector-effect": "non-scaling-stroke",
  });
  el("text", { x: px, y: py, "text-anchor": "middle", "font-size": "7.5", fill: "#ffd447" }, t("help.you"));
}

function renderHelp(): void {
  $("#helpTitle").textContent = t("help.title", { level: L.short });
  const r = $("#hrules");
  r.replaceChildren();
  const H4 = (t: string) => r.appendChild(h("h4", { text: t }));
  const UL = (items: string[]) => r.appendChild(h("ul", {}, ...items.map((t) => h("li", { text: t }))));

  H4(t("help.timeHead"));
  const rows: [string, string, string][] = [
    [
      t("help.idle"),
      t("help.idleWhen", { sec: idleSecNow() }) + (L.id === "or" ? t("help.idleOr") : ""),
      t("help.idleCost"),
    ],
    [t("help.walk"), t("help.walkWhen"), t("help.walkCost")],
    [t("help.talk"), t("help.talkWhen"), t("help.talkCost")],
    [t("help.act"), t("help.actWhen"), t("help.actCost")],
    [t("help.pause"), t("help.pauseWhen"), t("help.pauseCost")],
  ];
  r.appendChild(h("table", {}, ...rows.map((row) => h("tr", {}, ...row.map((cell) => h("td", { text: cell }))))));
  UL([t("help.noWait"), t("help.span", { start: clock(0), end: clock(L.maxT ?? 75) })]);
  H4(t("help.ctrlHead"));
  UL([t("help.ctrl1"), t("help.ctrl2"), t("help.ctrl3"), t("help.ctrl4"), t("help.ctrl5"), t("help.ctrl6")]);
  if (L.helpRules) {
    H4(t("help.rulesHead"));
    UL(L.helpRules);
  }

  const groups = helpGroups();
  drawHelpMap(groups);
  const warn = L.helpWarn ?? [];
  $("#hlegend").replaceChildren(
    ...groups.map((gr, i) => {
      const name = h("b", { text: interactName(gr.id) });
      name.style.fontWeight = "400";
      const label = h("span", {}, name);
      const d = L.help?.[gr.id];
      if (d) label.appendChild(h("span", { className: "d", text: `${t("help.legendSep")}${d}` }));
      return h(
        "li",
        {},
        h("span", { className: "n" + (warn.includes(gr.id) ? " red" : ""), text: String(i + 1) }),
        label,
      );
    }),
  );
}

export function openHelp(after?: () => void): void {
  if (!S.live) return;
  helpAfter = after ?? null;
  closeZoom();
  clearMovement();
  renderHelp();
  $("#help").classList.add("show");
  $("#helpGo").focus();
}

export function closeHelp(): void {
  $("#help").classList.remove("show");
  const f = helpAfter;
  helpAfter = null;
  f?.();
}

/** First time a level starts this page load, show help before the clock runs. */
export function showHelpOnce(levelId: string, then: () => void): void {
  if (helpSeen.has(levelId)) then();
  else {
    helpSeen.add(levelId);
    openHelp(then);
  }
}

export function resetHelp(): void {
  helpAfter = null;
}

export function initHelp(): void {
  $("#helpBtn").onclick = () => openHelp();
  $("#helpClose").onclick = closeHelp;
  $("#helpGo").onclick = closeHelp;
  const tabs = [...document.querySelectorAll<HTMLButtonElement>("#help .tabs .btn")];
  for (const b of tabs)
    b.onclick = () => {
      for (const x of tabs) x.classList.toggle("on", x === b);
      for (const sec of document.querySelectorAll(".hgrid > section"))
        sec.classList.toggle("on", sec.id === b.dataset.tab);
    };
}
